import React from "react";

const parseInlineFormatting = (text: string, baseKey: string = ''): React.ReactNode[] => {
  const elements: React.ReactNode[] = [];
  let currentIndex = 0;

  while (currentIndex < text.length) {
    const boldMatch = text.slice(currentIndex).match(/^\*\*(.*?)\*\*/);
    if (boldMatch) {
      elements.push(
        <strong key={`${baseKey}bold-${currentIndex}`}>{boldMatch[1]}</strong>
      );
      currentIndex += boldMatch[0].length;
      continue;
    }

    const underscoreMatch = text.slice(currentIndex).match(/^__(.*?)__/);
    if (underscoreMatch) {
      elements.push(
        <strong key={`${baseKey}bold-${currentIndex}`}>{underscoreMatch[1]}</strong>
      );
      currentIndex += underscoreMatch[0].length;
      continue;
    }

    // Handle inline code (`code`)
    const codeMatch = text.slice(currentIndex).match(/^`([^`]+)`/);
    if (codeMatch) {
      elements.push(
        <code
          key={`${baseKey}code-${currentIndex}`}
          style={{
            background: "rgba(255,255,255,0.1)",
            padding: "2px 4px",
            borderRadius: "3px",
            fontFamily: "monospace",
          }}
        >
          {codeMatch[1]}
        </code>
      );
      currentIndex += codeMatch[0].length;
      continue;
    }

    // Handle regular text - but skip single * characters that might be bullet points
    const nextSpecialChar = text.slice(currentIndex).search(/[\*\`_]/);
    if (nextSpecialChar === -1) {
      // No more special characters, add remaining text
      elements.push(text.slice(currentIndex));
      break;
    } else if (nextSpecialChar > 0) {
      // Add text before the special character
      elements.push(text.slice(currentIndex, currentIndex + nextSpecialChar));
      currentIndex += nextSpecialChar;
    } else {
      // Special character at current position
      const char = text[currentIndex];
      if (char === "*" && text.slice(currentIndex + 1).startsWith("*")) {
        // This is part of bold formatting (**), skip it
        currentIndex++;
      } else if (char === "*" && text.slice(currentIndex + 1).startsWith(" ")) {
        // This might be a bullet point, skip it
        currentIndex++;
      } else {
        // Add as plain text
        elements.push(char);
        currentIndex++;
      }
    }
  }

  return elements;
};

// Check if a line is a selectable option like (A), (B), (C), etc.
const isOptionLine = (line: string): boolean => {
  return /^\([A-Z]\)\s+/.test(line.trim());
};

// Parse option letter and text from an option line
const parseOption = (line: string): { letter: string; text: string } | null => {
  const match = line.trim().match(/^\(([A-Z])\)\s+(.*)/);
  if (!match) return null;
  return { letter: match[1], text: match[2] };
};

// Extract options from message content for rendering as buttons
export const extractOptions = (content: string): { letter: string; text: string }[] => {
  const lines = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const options: { letter: string; text: string }[] = [];

  for (const line of lines) {
    if (isOptionLine(line)) {
      const option = parseOption(line);
      if (option) options.push(option);
    }
  }

  return options;
};

// Detect if message is multi-select — model outputs [MULTI-SELECT] tag
export const isMultiSelectMessage = (content: string): boolean => {
  return content.includes('[MULTI-SELECT]');
};

// Strip the [MULTI-SELECT] tag from display content
export const stripSelectionTags = (content: string): string => {
  return content.replace(/\[MULTI-SELECT\]/g, '').replace(/\n{3,}/g, '\n\n');
};

// Headers that introduce option lists — strip these from past messages alongside the options
const isOptionIntroLine = (line: string): boolean => {
  const trimmed = line.trim().replace(/\*\*/g, '').toLowerCase();
  const introPatterns = [
    'examples:', 'example:', 'is it:', 'is it', 'maybe something like:',
    'here are a few', 'here are some', 'choose from:', 'select one:',
    'options:', 'pick one:', 'such as:', 'for example:', 'like:',
    'a few examples', 'we need',
  ];
  return introPatterns.some(p => trimmed === p || trimmed.startsWith(p));
};

// Strip option lines AND their intro headers from content (for answered messages in chat history)
export const stripOptionLines = (content: string): string => {
  const lines = content
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

  // Find the first option line index — strip intro headers that appear just before it
  const firstOptionIdx = lines.findIndex(l => isOptionLine(l.trim()));

  return lines
    .filter((line, idx) => {
      if (isOptionLine(line.trim())) return false;
      // Strip intro headers that appear within 3 lines before the first option
      if (firstOptionIdx >= 0 && idx >= firstOptionIdx - 3 && idx < firstOptionIdx && isOptionIntroLine(line)) return false;
      return true;
    })
    .join("\n")
    .replace(/\[MULTI-SELECT\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Helper function to convert content to JSX elements with proper formatting
export const formatMessageContent = (content: string, onOptionSelect?: (text: string) => void, selectedOptions?: Set<string>): React.ReactNode => {
  let formattedContent = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Split into lines and process each line
  const lines = formattedContent.split("\n");
  const elements: React.ReactNode[] = [];

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      elements.push(<br key={`br-${index}`} />);
      return;
    }

    // Handle option lines (A), (B), (C) as clickable buttons
    if (isOptionLine(trimmedLine) && onOptionSelect) {
      const option = parseOption(trimmedLine);
      if (option) {
        const isSelected = selectedOptions?.has(option.text);
        elements.push(
          <button
            key={`option-${index}`}
            onClick={() => onOptionSelect(option.text)}
            className={`w-full text-left px-4 py-3 my-1 rounded-xl border transition-all duration-200 active:scale-[0.98] ${
              isSelected
                ? 'border-[#79c9cb] bg-gradient-to-r from-[#79c9cb]/25 to-[#4b7bdd]/25'
                : 'border-white/20 bg-transparent'
            }`}
          >
            <span className="font-semibold text-[#79c9cb] mr-2">
              {isSelected ? '✓' : `(${option.letter})`}
            </span>
            <span className="text-white">{option.text}</span>
          </button>
        );
        return;
      }
    }

    // Handle option lines without callback - render as styled text
    if (isOptionLine(trimmedLine) && !onOptionSelect) {
      const option = parseOption(trimmedLine);
      if (option) {
        elements.push(
          <div
            key={`option-text-${index}`}
            style={{ marginBottom: "4px", paddingLeft: "4px" }}
          >
            <span style={{ fontWeight: "bold", color: "#79c9cb", marginRight: "8px" }}>({option.letter})</span>
            <span>{option.text}</span>
          </div>
        );
        return;
      }
    }

    // Handle numbered lists (1. 2. 3. etc.)
    if (/^\d+\./.test(trimmedLine)) {
      const match = trimmedLine.match(/^(\d+)\.\s*(.*)/);
      if (match) {
        const [, number, text] = match;
        elements.push(
          <div
            key={`numbered-${index}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "4px",
            }}
          >
            <span
              style={{
                marginRight: "8px",
                marginTop: "2px",
                fontWeight: "bold",
              }}
            >
              {number}.
            </span>
            <span>{text}</span>
          </div>
        );
      }
      return;
    }

    // Handle bullet points (various formats)
    if (
      trimmedLine.startsWith("-") ||
      trimmedLine.startsWith("•") ||
      (trimmedLine.startsWith("*") && !trimmedLine.startsWith("**")) ||
      trimmedLine.startsWith("◦") ||
      (trimmedLine.startsWith("**") && trimmedLine.includes(":"))
    ) {
      let afterBullet: string;
      let isSpecialCase = false;

      if (trimmedLine.startsWith("**") && trimmedLine.includes(":")) {
        // Special case: "**Visual Cues:**" -> treat as bullet point with bold text
        const match = trimmedLine.match(/^\*\*(.*?)\*\*/);
        if (match) {
          afterBullet = match[1]; // Just use the captured text as-is
          isSpecialCase = true;
        } else {
          afterBullet = trimmedLine;
          isSpecialCase = false;
        }
      } else {
        // Normal bullet point
        afterBullet = trimmedLine.replace(/^[-•*◦]\s*/, "").trim();
      }

      elements.push(
        <div
          key={`bullet-${index}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginBottom: "4px",
          }}
        >
          <span style={{ marginRight: "8px", marginTop: "2px" }}>•</span>
          <span>
            {isSpecialCase ? (
              <strong>{afterBullet}</strong>
            ) : (
              parseInlineFormatting(afterBullet, `bullet-${index}-`)
            )}
          </span>
        </div>
      );
      return;
    }

    // Handle headers (lines that end with colon and are short)
    if (trimmedLine.endsWith(":") && trimmedLine.length < 50) {
      elements.push(
        <div
          key={`header-${index}`}
          style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "1.1em" }}
        >
          {trimmedLine}
        </div>
      );
      return;
    }

    // Handle emphasis and code (text wrapped in **, __, or `)
    if (
      trimmedLine.includes("**") ||
      trimmedLine.includes("__") ||
      trimmedLine.includes("`")
    ) {
      elements.push(
        <div key={`formatted-${index}`} style={{ marginBottom: "4px" }}>
          {parseInlineFormatting(trimmedLine, `formatted-${index}-`)}
        </div>
      );
      return;
    }

    // Handle regular lines
    elements.push(
      <div key={`line-${index}`} style={{ marginBottom: "4px" }}>
        {trimmedLine}
      </div>
    );
  });

  return <>{elements}</>;
};
